import { FormProvider } from 'react-hook-form';
import {
  Alert,
  AlertDescription,
  Button,
  CopyButton,
  Label,
} from '@tinct/web-components';
import { createConnectedInputHelpers } from '@tinct/web-form';
import {
  ReadSsoConnection,
  SsoOidcConnectionFormValues,
} from '@tinct/core-api-models';
import { InfoIcon } from 'lucide-react';
import { useSsoConnectionForm } from '../hooks/use-sso-connection-form';

const { ControlledTextInput, ControlledPasswordInput } =
  createConnectedInputHelpers<SsoOidcConnectionFormValues>();

export type SsoOidcConnectionFormProps = {
  organizationId: string;
  connection: ReadSsoConnection | undefined;
};

/** What an OpenID Connect provider is described by: its issuer, and the client it issued to Tinct. */
export function SsoOidcConnectionForm({
  organizationId,
  connection,
}: SsoOidcConnectionFormProps) {
  const { form, handleSubmit, isCreation, isSaving } = useSsoConnectionForm(
    organizationId,
    connection,
  );
  const rootError = form.formState.errors.root?.message;

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-6">
          {connection?.redirectUri && (
            <div className="flex flex-col gap-1.5">
              <Label>Redirect URI</Label>
              <div className="flex items-center gap-2 rounded-lg bg-muted/50 p-3">
                <span className="break-all font-mono text-xs">
                  {connection.redirectUri}
                </span>
                <CopyButton value={connection.redirectUri} />
              </div>
              <span className="text-xs text-muted-foreground">
                Register this address as the redirect (callback) URI of the
                Tinct application on your provider's side.
              </span>
            </div>
          )}

          <ControlledTextInput
            name="issuer"
            label="Issuer"
            placeholder="https://acme.okta.com"
            description="The provider's issuer URL. Tinct reads its OpenID configuration from it."
          />
          <ControlledTextInput
            name="clientId"
            label="Client ID"
            placeholder="0oa1b2c3d4e5f6g7h8i9"
          />
          <ControlledPasswordInput
            name="clientSecret"
            label="Client secret"
            placeholder={
              connection?.clientSecretSet
                ? 'Saved — type a new one to replace it'
                : 'Paste the client secret'
            }
          />

          <Alert>
            <InfoIcon />
            <AlertDescription>
              Changing the issuer or the client ID sends the connection back to
              draft: single sign-on is switched off and the requirement lifted
              until a new test sign-in succeeds. A new client secret does not:
              run a test sign-in to check it.
            </AlertDescription>
          </Alert>

          {rootError && <p className="text-sm text-destructive">{rootError}</p>}

          <div className="flex justify-end">
            <Button type="submit" isLoading={isSaving}>
              {isCreation ? 'Save identity provider' : 'Save changes'}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
